import {
  doc,
  serverTimestamp,
  setDoc,
  type DocumentReference,
} from "firebase/firestore";
import { db, auth, collection } from "./firebase";
import {
  getJourneyId,
  getJourneyPageLoad,
  getPageLoadId,
  getTabId,
  nextSeq,
} from "@/telemetry/ids";
import {
  getBuildTag,
  getCurrentPhase,
  getEnv,
  getRoute,
  getScanId,
  getTenant,
  getWidgetSessionId,
} from "@/telemetry/context";
import {
  deriveLegacyEvent,
  type Outcome,
  type Phase,
} from "@/telemetry/events";
import { newRowId } from "@/telemetry/ids";
import { markPending, markSettled } from "@/telemetry/outbox";

/**
 * Bump ONLY for a breaking change to the spine. This is the era discriminator:
 * legacy rows have no `schemaVersion` field at all, and Firestore cannot query
 * for a missing field, so `where schemaVersion == 1` is how you select modern
 * rows and a `timestamp` cutover is how you select legacy ones.
 * See `docs/telemetry/PLAN.md` §9.
 */
export const TELEMETRY_SCHEMA_VERSION = 1;

// Log types enum
export enum LogType {
  ERROR = "error",
  WARNING = "warning",
  INFO = "info",
}

// Interface for extra info
export interface ExtraLogInfo {
  [key: string]: any;
}

/** Typed-event options. See `logEvent()`. */
export interface LogEventOptions {
  /** Maps to the legacy `type` field on the row. There is no separate `level` field. */
  level?: LogType;
  /** Overrides the ambient phase. Omit to inherit `getCurrentPhase()`. */
  phase?: Phase | null;
  /** Only meaningful on `phase.exit` and `*.summary` events. */
  outcome?: Outcome | null;
  durationMs?: number | null;
  /** Typed payload. Goes to the INDEX-EXEMPT `data` field, never the spine. */
  data?: ExtraLogInfo;
}

/**
 * Logger function to add logs to Firestore
 * @param type - Type of log (error, warning, info)
 * @param message - Log message text
 * @param extraInfo - Optional additional information
 */
// Helper function to sanitize extra info.
//
// Nothing `undefined` may leave here. Firestore refuses it anywhere in a
// document, and a refused row is gone: `writeLog`'s catch only prints it.
// An object's undefined field is dropped; an array's becomes `null`, because
// dropping it would shift every index after it.
//
// `ancestors` holds the objects on the path from the root down to `obj`, so a
// reference back up that path, a cycle, becomes "[Circular]" instead of
// recursing until the stack overflows. It holds the path, not every object
// seen: an object referenced twice without a cycle is written out both times.
const sanitizeExtraInfo = (
  obj: any,
  ancestors: WeakSet<object> = new WeakSet()
): any => {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (ancestors.has(obj)) {
    return "[Circular]";
  }

  if (obj instanceof Error) {
    // `stack` is often undefined. Chromium puts none on the DOMExceptions it
    // rejects promises with (play(), getUserMedia, orientation lock), and
    // WebKit none on errors it creates with no script running (a fetch's
    // "Load failed"). Until this mapped it to `null`, every such row was lost.
    // `null` rather than a dropped key: Firestore can select
    // `extraInfo.stack == null` but never a missing field, so the kind of row
    // the old code lost can be counted from now on.
    return {
      name: obj.name ?? null,
      message: obj.message ?? null,
      stack: obj.stack ?? null,
    };
  }

  ancestors.add(obj);
  try {
    if (Array.isArray(obj)) {
      return obj.map((item) =>
        item === undefined ? null : sanitizeExtraInfo(item, ancestors)
      );
    }

    const sanitizedObj: { [key: string]: any } = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        if (value !== undefined) {
          sanitizedObj[key] = sanitizeExtraInfo(value, ancestors);
        }
      }
    }

    return sanitizedObj;
  } finally {
    ancestors.delete(obj);
  }
};

const consoleMethod = (type: LogType): "error" | "warn" | "info" =>
  type === LogType.ERROR
    ? "error"
    : type === LogType.WARNING
    ? "warn"
    : "info";

/**
 * The telemetry spine — the indexed, typed fields stamped onto EVERY row.
 *
 * Wrapped in its own try/catch and given a degraded fallback because a spine
 * failure must never cost us the log line itself. `getRoute()` and friends are
 * already individually defensive; this is the belt to their braces.
 *
 * `seq` is intentionally consumed OUTSIDE the try (by the caller) so that a
 * spine failure still burns a sequence number rather than silently producing
 * two rows with the same `seq`.
 *
 * See `docs/telemetry/PLAN.md` §3 for the field-by-field rationale.
 */
const buildSpine = (seq: number) => {
  try {
    return {
      schemaVersion: TELEMETRY_SCHEMA_VERSION,
      journeyId: getJourneyId(),
      tabId: getTabId(),
      pageLoadId: getPageLoadId(),
      journeyPageLoad: getJourneyPageLoad(),
      seq,
      scanId: getScanId(),
      widgetSessionId: getWidgetSessionId(),
      tenant: getTenant(),
      env: getEnv(),
      route: getRoute(),
      buildTag: getBuildTag(),
    };
  } catch {
    return {
      schemaVersion: TELEMETRY_SCHEMA_VERSION,
      journeyId: "unresolved",
      tabId: "unresolved",
      pageLoadId: "unresolved",
      journeyPageLoad: -1,
      seq,
      scanId: null,
      widgetSessionId: null,
      tenant: "dev",
      env: "dev",
      route: "unknown",
      buildTag: "unknown",
    };
  }
};

/**
 * What a refused payload is replaced with. Always storable, and small whatever
 * the refusal said: the reason is cut to 300 characters.
 */
const refusedPayload = (refusal: unknown) => ({
  payloadRefused: true,
  reason: String(
    refusal instanceof Error ? refusal.message : refusal
  ).slice(0, 300),
});

/**
 * One attempt at writing a row. Resolves `null` once Firestore has it, or with
 * the refusal if Firestore refused the document. Any other failure rejects.
 *
 * A refusal arrives two ways:
 * - Synchronously, from `setDoc` itself. It validates the whole document
 *   before sending anything, and throws there for every value it cannot
 *   store: a FirebaseError `invalid-argument` for `undefined`, nested arrays,
 *   functions and `__x__` keys, and a bare "INTERNAL ASSERTION FAILED" Error,
 *   with no code, for a symbol or a BigInt. A network failure never throws
 *   (writes queue and retry), so every synchronous throw here is a refusal,
 *   including one from building the row: a payload whose getter throws.
 * - Later, from the server, for limits the client does not check (a document
 *   over 1 MiB, too many index entries): a rejected write with code
 *   `invalid-argument`.
 */
const attemptWrite = async (
  ref: DocumentReference,
  rowId: string,
  buildRow: () => Record<string, unknown>
): Promise<{ refusal: unknown } | null> => {
  let write: Promise<void>;
  try {
    const row = buildRow();
    // Pending before the write goes out: if the page dies mid-flight, the
    // outbox must hold the row this attempt sent (see telemetry/outbox.ts).
    markPending(rowId, row);
    write = setDoc(ref, row);
  } catch (refusal) {
    return { refusal };
  }
  try {
    await write;
    return null;
  } catch (e) {
    if ((e as { code?: unknown } | null)?.code === "invalid-argument") {
      return { refusal: e };
    }
    throw e;
  }
};

/**
 * Write one row to `pwa_logs`.
 *
 * Shared by `logger()` (the untyped legacy path used by all 243 pre-existing
 * call sites) and `logEvent()` (the typed path). The only difference between
 * them is where `event`, `phase`, `outcome`, `durationMs` and `data` come from.
 *
 * If Firestore refuses the payload, the row is written once more under the
 * same id with the payload replaced by `{ payloadRefused, reason }`, so it
 * still lands with its message, type and spine. See `attemptWrite`.
 */
const writeLog = async (
  type: LogType,
  message: string,
  event: string,
  extraInfo: ExtraLogInfo | undefined,
  opts: LogEventOptions
): Promise<void> => {
  const seq = nextSeq();
  try {
    const browserData = getBrowserInfo();
    const osData = getOSInfo();

    const pwaVersion = process.env.REACT_APP_VERSION;

    // Everything but the payload, built once, so a degraded retry carries
    // exactly the envelope of the row that was refused: the same timestamp,
    // spine and phase, even if the server took seconds to refuse it.
    const envelope = {
      // --- legacy fields: NEVER rename, NEVER drop. Every runbooks playbook,
      // every bug dossier's signature_query, and ~2 years of history depend on
      // these exact names. See docs/telemetry/PLAN.md §2.
      type,
      message,
      timestamp: new Date(),
      userId: auth.currentUser?.uid || "anonymous",
      browser: browserData.browser,
      browserVersion: browserData.version,
      os: osData.os,
      osVersion: osData.version,
      pwaVersion,

      // --- telemetry spine (indexed) ---
      ...buildSpine(seq),
      event,
      // NOTE: there is deliberately no `level` field. It would be a byte-identical
      // duplicate of `type` above, costing index entries on every row for zero query
      // benefit — and a live (type, timestamp) composite index already exists in BOTH
      // Firebase projects, so error triage is already served. See docs/telemetry/PLAN.md 4.4.
      phase: opts.phase !== undefined ? opts.phase : getCurrentPhase(),
      outcome: opts.outcome !== undefined ? opts.outcome : null,
      durationMs: opts.durationMs !== undefined ? opts.durationMs : null,
    };

    // `payload` makes what the call site passed storable: sanitized on the
    // first attempt, replaced by the refusal marker on the retry. Only a
    // payload the call site actually passed is replaced.
    const row = (payload: (value: ExtraLogInfo) => unknown) => ({
      ...envelope,
      extraInfo: extraInfo ? payload(extraInfo) : {},

      // The only clock an investigator can trust across users. Do NOT order a
      // single journey by it — it resolves at commit, and commits reorder.
      // Order by (journeyPageLoad, seq). See PLAN.md §4.2.
      serverTs: serverTimestamp(),

      // --- payload (index-exempt) ---
      data: opts.data ? payload(opts.data) : {},
    });

    // setDoc with a CLIENT-generated id, not addDoc: a row persisted to the
    // outbox and re-sent on the next boot must overwrite the same document
    // rather than create a duplicate. Ids are random, so this does not
    // reintroduce key hotspotting. See telemetry/outbox.ts. A degraded
    // retry reuses the id for the same reason.
    const rowId = newRowId();
    const ref = doc(collection(db, "pwa_logs"), rowId);
    try {
      const first = await attemptWrite(ref, rowId, () =>
        row(sanitizeExtraInfo)
      );
      if (first) {
        // Once only. If the marker row is refused too, the envelope itself is
        // bad (a `pwaVersion` of undefined, say) and there is nothing left to
        // drop, so it fails like any other write.
        const second = await attemptWrite(ref, rowId, () =>
          row(() => refusedPayload(first.refusal))
        );
        if (second) throw second.refusal;
        console.error(
          "Failed to log to Firestore: payload refused, row written without it:",
          first.refusal
        );
      }
    } finally {
      markSettled(rowId);
    }

    console[consoleMethod(type)](
      `[${type.toUpperCase()}]`,
      message,
      extraInfo || opts.data || ""
    );
  } catch (error) {
    // Fallback to console if Firestore logging fails
    console.error("Failed to log to Firestore:", error);
    console[consoleMethod(type)](
      `[${type.toUpperCase()}]`,
      message,
      extraInfo || opts.data || ""
    );
  }
};

/**
 * Logger function to add logs to Firestore
 * @param type - Type of log (error, warning, info)
 * @param message - Log message text
 * @param extraInfo - Optional additional information
 *
 * Untyped path. `event` is derived from the message's `[Prefix]` via a CLOSED
 * map (`deriveLegacyEvent`), which gives these call sites coarse but genuinely
 * indexed queryability without risking the cardinality of an indexed field.
 * Prefer `logEvent()` for anything new.
 */
export const logger = async (
  type: LogType,
  message: string,
  extraInfo?: ExtraLogInfo
): Promise<void> => {
  return writeLog(type, message, deriveLegacyEvent(message), extraInfo, {});
};

/**
 * Typed path — the one to use for all new instrumentation.
 *
 * `event` must be a value from `Events` in `@/telemetry/events`, and it must
 * have an entry in `docs/telemetry/EVENTS.md` describing its payload, what a
 * bad value looks like, its denominator, and the version it shipped in. An
 * event without that entry cannot be interpreted by an analysis agent.
 *
 * @example
 * logEvent(Events.PHASE_EXIT, "alignment ended", {
 *   phase: Phases.ALIGNMENT,
 *   outcome: Outcomes.ABANDONED,
 *   durationMs: 42_310,
 *   data: { jointNeverInArea: ["left_wrist"] },
 * });
 */
export const logEvent = (
  event: string,
  message: string,
  opts: LogEventOptions = {}
): Promise<void> => {
  return writeLog(
    opts.level || LogType.INFO,
    message,
    event,
    undefined,
    opts
  );
};

// Order matters in both detectors below. Every iOS browser is forced onto
// WebKit and reports "Safari/604.x" in its UA, so iOS-specific browser
// tokens (CriOS / FxiOS / EdgiOS / OPiOS|OPT) must be matched BEFORE the
// generic Safari catch-all. Chromium-based desktop browsers (Edg / OPR /
// SamsungBrowser) all include "Chrome/x.y", so their distinctive tokens
// must be matched BEFORE the generic Chrome rule. For the OS detector,
// iOS UAs contain "like Mac OS X" in the platform tail, so iPhone/iPad/
// iPod detection must come BEFORE the /Mac OS X/ check; Android UAs
// contain "Linux", so Android must come BEFORE Linux.
const getBrowserInfo = (): { browser: string; version: string } => {
  const ua = navigator.userAgent;
  let m: RegExpMatchArray | null;

  if ((m = ua.match(/CriOS\/(\d+(?:\.\d+)+)/))) return { browser: "Chrome", version: m[1] };
  if ((m = ua.match(/FxiOS\/(\d+(?:\.\d+)+)/))) return { browser: "Firefox", version: m[1] };
  if ((m = ua.match(/EdgiOS\/(\d+(?:\.\d+)+)/))) return { browser: "Edge", version: m[1] };
  if ((m = ua.match(/(?:OPiOS|OPT)\/(\d+(?:\.\d+)+)/))) return { browser: "Opera", version: m[1] };

  if ((m = ua.match(/Edg\/(\d+(?:\.\d+)+)/))) return { browser: "Edge", version: m[1] };
  if ((m = ua.match(/OPR\/(\d+(?:\.\d+)+)/))) return { browser: "Opera", version: m[1] };
  if ((m = ua.match(/Opera\/(\d+(?:\.\d+)+)/))) return { browser: "Opera", version: m[1] };
  if ((m = ua.match(/SamsungBrowser\/(\d+(?:\.\d+)+)/))) return { browser: "Samsung Internet", version: m[1] };

  if ((m = ua.match(/Firefox\/(\d+(?:\.\d+)+)/))) return { browser: "Firefox", version: m[1] };
  if ((m = ua.match(/Chrome\/(\d+(?:\.\d+)+)/))) return { browser: "Chrome", version: m[1] };

  if (/Safari\//.test(ua)) {
    const v = ua.match(/Version\/(\d+(?:\.\d+)+)/);
    return { browser: "Safari", version: v ? v[1] : "unknown" };
  }

  return { browser: "unknown", version: "unknown" };
};

const getOSInfo = (): { os: string; version: string } => {
  const ua = navigator.userAgent;

  if (/iPad|iPhone|iPod/.test(ua)) {
    const m = ua.match(/OS (\d+(?:[._]\d+){1,2})/);
    return { os: "iOS", version: m ? m[1].replace(/_/g, ".") : "unknown" };
  }

  // iPadOS 13+ requests a "Macintosh; Intel Mac OS X" desktop UA on iPad
  // by default — indistinguishable from a real Mac via UA alone. Touch-
  // capable iPads expose maxTouchPoints > 1; Macs report 0.
  const maxTouchPoints =
    typeof navigator !== "undefined" &&
    typeof navigator.maxTouchPoints === "number"
      ? navigator.maxTouchPoints
      : 0;
  if (/Macintosh/.test(ua) && maxTouchPoints > 1) {
    return { os: "iOS", version: "unknown" };
  }

  if (/Android/.test(ua)) {
    const m = ua.match(/Android (\d+(?:\.\d+)*)/);
    return { os: "Android", version: m ? m[1] : "unknown" };
  }

  if (/Windows NT/.test(ua)) {
    // Windows 11 still reports as "Windows NT 10.0" — Microsoft never bumped
    // the kernel version. The "10" bucket therefore covers both 10 and 11.
    let version = "unknown";
    if (/Windows NT 10\.0/.test(ua)) version = "10";
    else if (/Windows NT 6\.3/.test(ua)) version = "8.1";
    else if (/Windows NT 6\.2/.test(ua)) version = "8";
    else if (/Windows NT 6\.1/.test(ua)) version = "7";
    else if (/Windows NT 6\.0/.test(ua)) version = "Vista";
    else if (/Windows NT 5\.1/.test(ua)) version = "XP";
    return { os: "Windows", version };
  }

  if (/Mac OS X/.test(ua)) {
    const m = ua.match(/Mac OS X (\d+(?:[._]\d+){1,2})/);
    return { os: "macOS", version: m ? m[1].replace(/_/g, ".") : "unknown" };
  }

  if (/Linux/.test(ua)) {
    return { os: "Linux", version: "unknown" };
  }

  return { os: "unknown", version: "unknown" };
};

// Convenience functions for different log types
export const logError = (
  message: string,
  extraInfo?: ExtraLogInfo
): Promise<void> => {
  return logger(LogType.ERROR, message, extraInfo);
};

export const logWarning = (
  message: string,
  extraInfo?: ExtraLogInfo
): Promise<void> => {
  return logger(LogType.WARNING, message, extraInfo);
};

export const logInfo = (
  message: string,
  extraInfo?: ExtraLogInfo
): Promise<void> => {
  return logger(LogType.INFO, message, extraInfo);
};

// Specialized logging functions for Start button diagnostics
export const logStartButtonState = (
  message: string,
  state: {
    cameraPermitted?: boolean;
    motionPermitted?: boolean;
    cameraStreaming?: boolean;
    motionEventsActive?: boolean;
    allImagesLoaded?: boolean;
    allAudiosLoaded?: boolean;
    modelsLoading?: boolean;
    poseModelLoaded?: boolean;
    objectModelLoaded?: boolean;
    isPortrait?: boolean;
    isRetryingAssets?: boolean;
    scanningState?: string;
    timeSinceNavigationMs?: number;
    timeSinceDisabledMs?: number;
  }
): Promise<void> => {
  return logger(LogType.INFO, message, {
    category: "start_button_diagnostics",
    ...state,
  });
};

export const logAssetLoadingTiming = (
  assetType: "images" | "audios" | "models",
  event: "start" | "complete" | "failed" | "retry",
  extraInfo?: ExtraLogInfo
): Promise<void> => {
  return logger(LogType.INFO, `Asset loading ${event}: ${assetType}`, {
    category: "asset_loading_timing",
    assetType,
    event,
    timestamp: Date.now(),
    ...extraInfo,
  });
};
